import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import moment from 'moment';

import {
  adminCreateGroupTutorRateVersion,
  adminUpdateGroupTutorRateVersion,
  GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES,
  IGroupTutorRateVersion,
  TAdminCreateGroupTutorRateVersion,
  TAdminUpdateGroupTutorRateVersion,
  TGroupTutorRateVersionConditionTypes,
  TGroupTutorRateVersionRegistrationDateConditionTypes,
} from '../../../../../../../store/V2/GroupTutorRateVersionsSlice';
import { capitalizeFirstLetter } from '../../../../../../../helper';
import { TPTextField } from '../../../../../../../components/Inputs';
import AddGroupTutorRateVersionConditionModal from './AddGroupTutorRateVersionConditionModal';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../../components/V2/Buttons';

interface AddGroupTutorRateVersionModalProps {
  open: boolean;
  onClose: () => void;
  groupTutorRateVersion?: IGroupTutorRateVersion;
  readOnly?: boolean;
  duplicate?: boolean;
}

export type TCustomRegistrationDateCondition = {
  _id: string;
  type: TGroupTutorRateVersionConditionTypes;
  logic: TGroupTutorRateVersionRegistrationDateConditionTypes;
  date: string;
};

export type ICustomCondition = TCustomRegistrationDateCondition;

type TState = {
  name: string;
  hideToUser: boolean;
  selectedConditions: ICustomCondition[];
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  name: '',
  hideToUser: false,
  selectedConditions: [],
};

const AddGroupTutorRateVersionModal: FunctionComponent<AddGroupTutorRateVersionModalProps> = ({
  open,
  onClose,
  groupTutorRateVersion,
  duplicate,
  readOnly,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isAddConditionModalOpen, setAddConditionModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const isSubmitDisabled = !state.name.trim().length;

  let mode: 'edit' | 'new' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Rate Version';

  if (groupTutorRateVersion) {
    mode = 'edit';
    modalTitle = 'Edit Rate Version';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Rate Version Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Rate Version';
    }
  }

  useEffect(() => {
    if (mode !== 'new' && groupTutorRateVersion) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (groupTutorRateVersion.name) {
          newStateData.name = groupTutorRateVersion.name;
        }

        if (groupTutorRateVersion.hideToUser) {
          newStateData.hideToUser = groupTutorRateVersion.hideToUser;
        }

        if (
          groupTutorRateVersion.conditions &&
          groupTutorRateVersion.conditions.length
        ) {
          newStateData.selectedConditions = groupTutorRateVersion.conditions;
        }

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleAddCondition = (condition: ICustomCondition) => {
    setState((prevState) => ({
      ...prevState,
      selectedConditions: [...prevState.selectedConditions, condition],
    }));
  };

  const removeSelectedCondition = (condition: ICustomCondition) => {
    setState((prevState) => ({
      ...prevState,
      selectedConditions: [...prevState.selectedConditions].filter(
        (item) => item._id !== condition._id
      ),
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateGroupTutorRateVersion = {
        name: state.name,
        hideToUser: state.hideToUser,
      };

      if (state.selectedConditions.length) {
        apiData.conditions = state.selectedConditions.map((item) => {
          return {
            type: item.type,
            date: item.date,
            logic: item.logic,
          };
        });
      }

      const res: any = await dispatch(
        adminCreateGroupTutorRateVersion(apiData)
      );

      if (res.payload) {
        onClose();
      }
    } else if (mode === 'edit' && groupTutorRateVersion) {
      const apiData: TAdminUpdateGroupTutorRateVersion = {
        id: groupTutorRateVersion._id,
        name: state.name,
        hideToUser: state.hideToUser,
      };

      if (state.selectedConditions.length) {
        apiData.conditions = state.selectedConditions.map((item) => {
          return {
            type: item.type,
            date: item.date,
            logic: item.logic,
          };
        });
      }

      const res: any = await dispatch(
        adminUpdateGroupTutorRateVersion(apiData)
      );

      if (res.payload) {
        onClose();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end">
                <FormControlLabel
                  label="Hide to user"
                  control={
                    <Switch
                      disabled={mode === 'view'}
                      checked={state.hideToUser}
                      onChange={(event, value) =>
                        updateStateAttributes({
                          hideToUser: value,
                        })
                      }
                    />
                  }
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Name *"
                  placeholder="Enter here..."
                  value={state.name}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="14px" fontWeight={600}>
                  Conditions *
                </Typography>
                {mode !== 'view' ? (
                  <DarkButton
                    disableElevation
                    variant="contained"
                    color="inherit"
                    onClick={() => setAddConditionModalOpen(true)}
                    sx={{ ...defaultButtonStyles }}
                  >
                    Add New Condition
                  </DarkButton>
                ) : null}
              </Stack>
            </Grid>
            {state.selectedConditions.map((conditionItem, index) => {
              if (
                conditionItem.type ===
                GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES.REGISTRATION_DATE
              ) {
                let typeName = capitalizeFirstLetter(
                  conditionItem.type.replaceAll('_', ' ').toLowerCase()
                );

                typeName += ` - ${capitalizeFirstLetter(
                  conditionItem.logic.replaceAll('_', ' ').toLowerCase()
                )}`;

                typeName += ` - ${moment(conditionItem.date).format(
                  'DD MMM YYYY hh:mm A'
                )}`;

                return (
                  <Grid item xs={12} key={`condition-item-${index}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography>{typeName}</Typography>
                      {mode !== 'view' ? (
                        <IconButton
                          onClick={() => removeSelectedCondition(conditionItem)}
                        >
                          <DeleteIcon sx={{ color: '#1E293B' }} />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </Grid>
                );
              }

              return null;
            })}
            {!state.selectedConditions.length ? (
              <Grid item xs={12}>
                <Typography fontSize="14px">
                  No conditions are available
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </div>
        {/* MODALS */}
        {isAddConditionModalOpen ? (
          <AddGroupTutorRateVersionConditionModal
            open={isAddConditionModalOpen}
            onClose={() => setAddConditionModalOpen(false)}
            onSubmit={handleAddCondition}
          />
        ) : null}
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AddGroupTutorRateVersionModal;
